import { isNullish } from '@nx/stdlib';

import type { DbmsDetailsResponse, DbmsResponse, License } from '../api-types';
import type { Dbms, DbmsDetails, License as LicenseType } from '../types/topology';

export function transformGetDbmssResponse(res: DbmsResponse[]): Dbms[] {
  return res.map(
    (dbms): Dbms => ({
      dbmsId: dbms.dbms_id,
      name: dbms.name,
      status: dbms.status,
      tokenCreatedBy: dbms.token_created_by,
    }),
  );
}

function transformLicense(license?: License): LicenseType | undefined {
  if (isNullish(license)) {
    return undefined;
  }
  return {
    type: license.type,
    state: license.state,
    daysLeftOnTrial: license.days_left_on_trial,
    totalTrialDays: license.total_trial_days,
  };
}

export function transformGetDbmsDetailsResponse(dbms: DbmsDetailsResponse): DbmsDetails {
  return {
    dbmsId: dbms.dbms_id,
    name: dbms.name,
    version: dbms.version,
    status: dbms.status,
    tokenCreatedBy: dbms.token_created_by,
    edition: dbms.edition,
    packaging: dbms.packaging,
    license: transformLicense(dbms.license),
    bloomLicense: transformLicense(dbms.bloom_license),
    gdsLicense: transformLicense(dbms.gds_license),
    databases: dbms.databases.map((database) => ({
      databaseId: database.database_id,
      name: database.name,
    })),
    servers: dbms.servers.map((server) => ({
      address: server.address,
      databaseInstances: server.database_instances.map((instance) => ({
        serverId: server.server_id,
        databaseName: instance.database_name,
        access: instance.access,
        aliases: instance.aliases,
        creationTime: instance.creation_time,
        currentPrimariesCount: instance.current_primaries_count,
        currentSecondariesCount: instance.current_secondaries_count,
        currentStatus: instance.current_status,
        databaseId: instance.database_id,
        default: instance.default,
        lastStartTime: instance.last_start_time,
        requestedPrimariesCount: instance.requested_primaries_count,
        requestedSecondariesCount: instance.requested_secondaries_count,
        requestedStatus: instance.requested_status,
        role: instance.role,
        store: instance.store,
        writer: instance.writer,
        lastCommittedTxn: instance.last_committed_txn,
        replicationLag: instance.replication_lag,
      })),
      health: server.health,
      lastPing: server.last_ping,
      serverId: server.server_id,
      modeConstraint: server.mode_constraint,
      version: server.version,
      status: server.status,
      license: transformLicense(server.license),
      bloomLicense: transformLicense(server.bloom_license),
      gdsLicense: transformLicense(server.gds_license),
    })),
  };
}
